import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaUserShield, FaSearch, FaCheck, FaCrown, FaUser } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader/PageHeader';
import { MODULES } from '../../config/modulePermissions';
import * as userAccessService from '../../services/userAccess.service';
import { useAuth } from '../../context/AuthContext';
import './UserAccessControl.css';

const ROLE_META = {
  super_admin: { label: 'Super Admin', cls: 'super-admin', Icon: FaUserShield },
  admin: { label: 'Admin', cls: 'admin', Icon: FaCrown },
  'non-admin': { label: 'Non-admin', cls: 'non-admin', Icon: FaUser },
};

const RolePill = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META['non-admin'];
  const { Icon } = meta;
  return (
    <span className={`uac-role-pill ${meta.cls}`}>
      <Icon size={10} /> {meta.label}
    </span>
  );
};

const hasFullAccessRole = (role) => role === 'super_admin' || role === 'admin';

const UserAccessControl = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [draft, setDraft] = useState(() => new Set());
  const [savingPerms, setSavingPerms] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { users: list } = await userAccessService.getUsers();
      setUsers(list);
    } catch (e) {
      setError(
        e.response?.status === 403
          ? 'Only the workspace admin can manage user access.'
          : 'Failed to load users.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) || null,
    [users, selectedUserId],
  );

  const selectUser = (u) => {
    setSelectedUserId(u.id);
    setDraft(new Set(u.permissions || []));
    setMessage(null);
  };

  const toggleModule = (key) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.email, u.roleType].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [users, search]);

  const isSelectedSuper = selectedUser?.roleType === 'super_admin';
  const isSelectedFullAccess = hasFullAccessRole(selectedUser?.roleType);
  // Only the super admin changes roles, and never the super admin's own role.
  const canManageRole = isSuperAdmin && !isSelectedSuper;

  const handleSavePermissions = async () => {
    if (!selectedUser || isSelectedFullAccess) return;
    setSavingPerms(true);
    setMessage(null);
    try {
      const permissions = MODULES.map((m) => m.key).filter((k) => draft.has(k));
      const result = await userAccessService.updateUserPermissions(selectedUser.id, permissions);
      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? { ...u, permissions: result.permissions } : u)),
      );
      setDraft(new Set(result.permissions));
      setMessage({ type: 'success', text: 'Access updated successfully.' });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.message || 'Failed to update access.' });
    } finally {
      setSavingPerms(false);
    }
  };

  const handleChangeRole = async (nextRole) => {
    if (!selectedUser || selectedUser.roleType === nextRole) return;
    setSavingRole(true);
    setMessage(null);
    try {
      const result = await userAccessService.updateUserRole(selectedUser.id, nextRole);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id
            ? { ...u, roleType: result.roleType, permissions: result.permissions }
            : u,
        ),
      );
      setDraft(new Set(result.permissions));
      setMessage({ type: 'success', text: `Role changed to ${result.roleType}.` });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.message || 'Failed to change role.' });
    } finally {
      setSavingRole(false);
    }
  };

  const grantedCount = (u) => (hasFullAccessRole(u.roleType) ? MODULES.length : (u.permissions?.length || 0));

  return (
    <div className="page-shell">
      <PageHeader
        icon={<FaUserShield size={22} />}
        title="User Access Control"
        subtitle="Grant or revoke module access for each user. Admins have full read/write access."
      />

      <div className="uac-layout">
        {/* Users list */}
        <div className="uac-users-card">
          <div className="uac-users-head">
            <h6 className="uac-card-title mb-0">Users {users.length > 0 && `(${users.length})`}</h6>
            <div className="uac-search">
              <FaSearch className="uac-search-icon" />
              <input
                type="text"
                placeholder="Search name, email, role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="uac-empty">Loading users…</div>
          ) : error ? (
            <div className="alert alert-danger m-3">{error}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="uac-empty">No users found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table uac-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>SR.NO</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role Type</th>
                    <th>Access</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, index) => (
                    <tr
                      key={u.id}
                      className={selectedUserId === u.id ? 'is-selected' : ''}
                      onClick={() => selectUser(u)}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <span className="fw-semibold">{u.name}</span>
                        {currentUser?.id === u.id && <span className="uac-you-tag">you</span>}
                      </td>
                      <td className="text-muted">{u.email}</td>
                      <td>
                        <RolePill role={u.roleType} />
                      </td>
                      <td className="text-muted">
                        {hasFullAccessRole(u.roleType) ? 'All modules' : `${grantedCount(u)} / ${MODULES.length}`}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectUser(u);
                          }}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Permission editor */}
        <div className="uac-editor-card">
          {!selectedUser ? (
            <div className="uac-editor-empty">
              <FaUserShield size={40} className="text-muted mb-3" />
              <h6 className="fw-semibold">Select a user</h6>
              <p className="text-muted small mb-0">
                Pick a user from the list to view and manage their module access.
              </p>
            </div>
          ) : (
            <>
              <div className="uac-editor-head">
                <div>
                  <h6 className="uac-card-title mb-1 d-flex align-items-center gap-2">
                    {selectedUser.name}
                    <RolePill role={selectedUser.roleType} />
                  </h6>
                  <p className="text-muted small mb-0">{selectedUser.email}</p>
                </div>
                {canManageRole && (
                  <div className="uac-role-switch">
                    <span className="uac-role-switch-label">Role</span>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn ${isSelectedFullAccess ? 'btn-dark' : 'btn-outline-dark'}`}
                        disabled={savingRole || isSelectedFullAccess}
                        onClick={() => handleChangeRole('admin')}
                      >
                        <FaCrown size={11} className="me-1" /> Admin
                      </button>
                      <button
                        type="button"
                        className={`btn ${!isSelectedFullAccess ? 'btn-dark' : 'btn-outline-dark'}`}
                        disabled={savingRole || !isSelectedFullAccess}
                        onClick={() => handleChangeRole('non-admin')}
                      >
                        <FaUser size={11} className="me-1" /> Non-admin
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {message && (
                <div className={`alert alert-${message.type} py-2 px-3 uac-message`}>{message.text}</div>
              )}

              {isSelectedSuper ? (
                <div className="uac-admin-note uac-super-note">
                  <FaUserShield className="me-2" />
                  This user is the <strong>Super Admin</strong> — full read/write access to every
                  module. This role is permanent and cannot be changed or edited by anyone.
                </div>
              ) : isSelectedFullAccess ? (
                <div className="uac-admin-note">
                  <FaCrown className="me-2" />
                  This user is a workspace <strong>admin</strong> and has full read/write access to
                  every module.{' '}
                  {isSuperAdmin
                    ? 'Switch the role to Non-admin to grant access selectively.'
                    : 'Only the Super Admin can change roles.'}
                </div>
              ) : (
                <p className="uac-hint">
                  Tick the modules this user should be able to access. Unticked modules are hidden from
                  their menu and blocked even via direct URL.
                </p>
              )}

              {!isSelectedSuper && (
                <div className="uac-modules-grid">
                  {MODULES.map((m) => {
                    const checked = isSelectedFullAccess || draft.has(m.key);
                    return (
                      <label
                        key={m.key}
                        className={`uac-module-card ${checked ? 'checked' : ''} ${isSelectedFullAccess ? 'locked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isSelectedFullAccess}
                          onChange={() => toggleModule(m.key)}
                        />
                        <span className="uac-module-check">
                          <FaCheck size={11} />
                        </span>
                        <span className="uac-module-label">{m.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {!isSelectedFullAccess && (
                <div className="uac-editor-actions">
                  <span className="text-muted small">
                    {draft.size} of {MODULES.length} modules selected
                  </span>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={savingPerms}
                    onClick={handleSavePermissions}
                  >
                    {savingPerms ? 'Saving…' : 'Save Access'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAccessControl;
